import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface AuthenticatedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * AuthenticatedRoute component that redirects already authenticated users
 * away from pages like login, signup, etc. to prevent confusion.
 * 
 * @param children - The component to render if user is not authenticated
 * @param redirectTo - Where to redirect authenticated users (defaults to '/')
 */
export function AuthenticatedRoute({ 
  children, 
  redirectTo = '/' 
}: AuthenticatedRouteProps) {
  const { 
    currentUser, 
    userProfile, 
    currentTenant, 
    loading 
  } = useAuth();
  const [shouldRedirect, setShouldRedirect] = useState<boolean | null>(null);

  useEffect(() => {
    if (loading) return;

    // If user is authenticated and has a valid profile/tenant, redirect them
    const timeoutId = setTimeout(() => {
      if (currentUser && userProfile && currentTenant && userProfile.is_active) {
        setShouldRedirect(true);
      } else {
        setShouldRedirect(false);
      }
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [loading, currentUser, userProfile, currentTenant]);

  // Show loading while checking authentication status
  if (loading || shouldRedirect === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Redirect authenticated users to the dashboard
  if (shouldRedirect) {
    return <Navigate to={redirectTo} replace />;
  }

  // Show the login page for unauthenticated users
  return <>{children}</>;
}
